// src/components/forms/FormTextarea/FormTextarea.tsx
import { forwardRef, useState } from "react";
import clsx from "clsx";
import type { FormTextareaProps } from "./formTextarea.types";
import {
  getFormTextareaContainerClasses,
  getFormTextareaLabelClasses,
  getFormTextareaClasses,
  getIconContainerClasses,
  getErrorMessageClasses,
  getHelperTextClasses,
  getCharCountClasses,
} from "./formTextarea.definitions";

const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  (
    {
      label,
      error,
      required = false,
      icon,
      helperText,
      className,
      textareaClassName,
      id,
      maxLength,
      showCharCount = false,
      rows = 4,
      resize = "vertical",
      ...props
    },
    ref
  ) => {
    const textareaId =
      id || `form-textarea-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = Boolean(error);
    const hasIcon = Boolean(icon);
    
    const [charCount, setCharCount] = useState(
      typeof props.value === 'string' ? props.value.length : 
      typeof props.defaultValue === 'string' ? props.defaultValue.length : 0
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCharCount(e.target.value.length);
      props.onChange?.(e);
    };

    return (
      <div className={getFormTextareaContainerClasses(className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={textareaId}
            className={getFormTextareaLabelClasses(required, hasError)}
          >
            {label}
          </label>
        )}

        {/* Textarea Container */}
        <div className="relative">
          {/* Ícone (se fornecido) */}
          {icon && <div className={getIconContainerClasses()}>{icon}</div>}

          {/* Textarea */}
          <textarea
            ref={ref}
            id={textareaId}
            rows={rows}
            className={clsx(
              getFormTextareaClasses(hasError, hasIcon, resize),
              textareaClassName
            )}
            aria-invalid={hasError}
            aria-describedby={
              error
                ? `${textareaId}-error`
                : helperText
                ? `${textareaId}-helper`
                : undefined
            }
            maxLength={maxLength}
            onChange={handleChange}
            {...props}
          />
        </div>

        {/* Character Count */}
        {showCharCount && maxLength && (
          <div className="flex justify-end">
            <span className={getCharCountClasses(charCount, maxLength)}>
              {charCount}/{maxLength}
            </span>
          </div>
        )}

        {/* Mensagem de Erro */}
        {error && (
          <p
            id={`${textareaId}-error`}
            className={getErrorMessageClasses()}
            role="alert"
          >
            {error}
          </p>
        )}

        {/* Texto de Ajuda */}
        {helperText && !error && (
          <p id={`${textareaId}-helper`} className={getHelperTextClasses()}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormTextarea.displayName = "FormTextarea";

export default FormTextarea;